/**
 * <rotating-labels>: on phones the product labels (custom.badges: "Beeswax barrier", "Grass-fed tallow", ...) are one
 * label whose text takes turns every 6 seconds, with the announcement bar's transition (as the buyer names in
 * assets/rotating-names.js): the text rises 10px and fades out in 0.25s, the next comes up from 10px below in 0.4s,
 * and the label eases to the width of the new text. From 700px all labels show side by side, as before.
 *
 * Markup (snippets/product-badges, snippets/product-gallery): the usual labels for wide screens, plus one
 * .rotating-labels__pill holding every text as a [data-label] (all but the first [hidden]) in .rotating-labels__text,
 * whose width the script sets. The element itself is display: contents, so everything stays in its own list.
 *
 * One shared clock turns every element on the page on the same beat (the cards of a collection change together),
 * runs on phones only and pauses in a background tab. Without motion the texts still take turns, without transition.
 */

const LEAVE = { duration: 250, easing: 'cubic-bezier(0.55, 0.055, 0.675, 0.19)', fill: 'forwards' };
const ENTER = { duration: 400, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)' };
const INTERVAL = 6000;

const phone = window.matchMedia('(max-width: 699px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const elements = new Set();
let timer;

const updateClock = () => {
  clearInterval(timer);
  timer = undefined;

  if (phone.matches && !document.hidden && elements.size > 0) {
    timer = setInterval(() => elements.forEach((element) => element.next()), INTERVAL);
  }
};

phone.addEventListener('change', () => {
  // The pill only has a size on a phone, so it is measured again when the screen becomes one
  elements.forEach((element) => element.fit({ instant: true }));
  updateClock();
});

document.addEventListener('visibilitychange', updateClock);

class RotatingLabels extends HTMLElement {
  #index = 0;
  #busy = false;

  connectedCallback() {
    this.text = this.querySelector('.rotating-labels__text');
    this.labels = Array.from(this.text?.querySelectorAll('[data-label]') ?? []);

    if (this.labels.length < 2) return;

    this.#index = Math.max(0, this.labels.findIndex((label) => !label.hidden));
    elements.add(this);

    // The first width is set without the ease (and again once the web font is in, which changes it), so nothing
    // moves on load
    this.fit({ instant: true });
    document.fonts?.ready.then(() => this.fit({ instant: true }));
    updateClock();
  }

  disconnectedCallback() {
    elements.delete(this);
    updateClock();
  }

  fit({ instant = false } = {}) {
    const label = this.labels?.[this.#index];

    if (!label || !phone.matches) return;

    if (instant) this.text.dataset.settling = '';

    this.text.style.width = `${label.getBoundingClientRect().width}px`;

    if (instant) {
      void this.text.offsetWidth;
      delete this.text.dataset.settling;
    }
  }

  async next() {
    if (this.#busy || !this.isConnected) return;

    const from = this.labels[this.#index];
    this.#index = (this.#index + 1) % this.labels.length;
    const to = this.labels[this.#index];

    if (reducedMotion.matches) {
      from.hidden = true;
      to.hidden = false;
      this.fit();
      return;
    }

    this.#busy = true;

    try {
      const leave = from.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-10px)' }], LEAVE);
      // Where the page draws no frames (a covered window) the animation would never end: swap after 0.6s regardless
      await Promise.race([leave.finished, new Promise((resolve) => setTimeout(resolve, 600))]);
      from.hidden = true;
      leave.cancel();
      to.hidden = false;
      this.fit();
      to.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'translateY(0)' }], ENTER);
    } catch {
      // The animation was cancelled
    } finally {
      this.#busy = false;
    }
  }
}

if (!window.customElements.get('rotating-labels')) {
  window.customElements.define('rotating-labels', RotatingLabels);
}
